
import React, { useState, useEffect } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { GitMerge, Users, TreePine, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { PotentialDuplicatesList } from './relationships/PotentialDuplicatesList';
import { HierarchyViewer } from './relationships/HierarchyViewer';
import { ManualLinkingControls } from './relationships/ManualLinkingControls';

interface PotentialDuplicate {
  record1_id: string;
  record1_creator: string;
  record1_handler: string;
  record1_type: string;
  record2_id: string;
  record2_creator: string;
  record2_handler: string;
  record2_type: string;
  match_type: string;
  status: string;
}

interface HierarchyRecord {
  id: string;
  creator_name: string;
  social_media_handler: string;
  social_media_type: string;
  parent_id: string | null;
  children?: HierarchyRecord[];
}

interface ParentChildRelationshipManagerProps {
  onDataChange?: () => void;
}

export const ParentChildRelationshipManager: React.FC<ParentChildRelationshipManagerProps> = ({
  onDataChange
}) => {
  const [potentialDuplicates, setPotentialDuplicates] = useState<PotentialDuplicate[]>([]);
  const [hierarchyData, setHierarchyData] = useState<HierarchyRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [pendingUpdates, setPendingUpdates] = useState<Set<string>>(new Set());
  const [counts, setCounts] = useState({
    potentialDuplicates: 0,
    existingRelationships: 0
  });
  const { toast } = useToast();

  const fetchPotentialDuplicates = async () => {
    try {
      const { data, error } = await supabase.rpc('get_potential_duplicates');
      if (error) throw error;
      
      const duplicates = data as PotentialDuplicate[] || [];
      setPotentialDuplicates(duplicates);
      setCounts(prev => ({ ...prev, potentialDuplicates: duplicates.length }));
    } catch (error) {
      console.error('Error fetching potential duplicates:', error);
      toast({
        title: "Error",
        description: "Failed to fetch potential duplicates",
        variant: "destructive"
      });
    }
  };

  const fetchHierarchyData = async () => {
    try {
      const { data, error } = await supabase
        .from('kol_db')
        .select('id, creator_name, social_media_handler, social_media_type, parent_id')
        .order('creator_name');
      
      if (error) throw error;

      // Build hierarchy structure
      const recordMap = new Map<string, HierarchyRecord>();
      const rootRecords: HierarchyRecord[] = [];

      // First pass: create all records
      data?.forEach(record => {
        const hierarchyRecord: HierarchyRecord = {
          id: record.id,
          creator_name: record.creator_name,
          social_media_handler: record.social_media_handler || '',
          social_media_type: record.social_media_type || '',
          parent_id: record.parent_id,
          children: []
        };
        recordMap.set(record.id, hierarchyRecord);
      });

      // Second pass: build hierarchy
      recordMap.forEach(record => {
        if (record.parent_id && recordMap.has(record.parent_id)) {
          const parent = recordMap.get(record.parent_id)!;
          parent.children!.push(record);
        } else if (!record.parent_id) {
          rootRecords.push(record);
        }
      });

      setHierarchyData(rootRecords);
      
      // Count existing relationships
      const relationshipCount = data?.filter(record => record.parent_id).length || 0;
      setCounts(prev => ({ ...prev, existingRelationships: relationshipCount }));
      
    } catch (error) {
      console.error('Error fetching hierarchy data:', error);
      toast({
        title: "Error",
        description: "Failed to fetch hierarchy data",
        variant: "destructive"
      });
    }
  };

  const fetchData = async () => {
    setIsLoading(true);
    await Promise.all([fetchPotentialDuplicates(), fetchHierarchyData()]);
    setIsLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleLinkRecords = async (parentId: string, childId: string) => {
    try {
      // Add to pending updates
      setPendingUpdates(prev => new Set(prev).add(childId));
      setIsProcessing(true);
      
      const { error } = await supabase
        .from('kol_db')
        .update({ parent_id: parentId })
        .eq('id', childId);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Records linked successfully",
      });

      // Refresh data
      await fetchData();
      if (onDataChange) onDataChange();
      
    } catch (error) {
      console.error('Error linking records:', error);
      toast({
        title: "Error",
        description: "Failed to link records",
        variant: "destructive"
      });
    } finally {
      // Remove from pending updates
      setPendingUpdates(prev => {
        const newSet = new Set(prev);
        newSet.delete(childId);
        return newSet;
      });
      setIsProcessing(false);
    }
  };

  const handleUnlinkRecord = async (childId: string) => {
    try {
      // Add to pending updates
      setPendingUpdates(prev => new Set(prev).add(childId));
      setIsProcessing(true);
      
      const { error } = await supabase
        .from('kol_db')
        .update({ parent_id: null })
        .eq('id', childId);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Record unlinked successfully",
      });

      // Refresh data
      await fetchData();
      if (onDataChange) onDataChange();
      
    } catch (error) {
      console.error('Error unlinking record:', error);
      toast({
        title: "Error",
        description: "Failed to unlink record",
        variant: "destructive"
      });
    } finally {
      // Remove from pending updates
      setPendingUpdates(prev => {
        const newSet = new Set(prev);
        newSet.delete(childId);
        return newSet;
      });
      setIsProcessing(false);
    }
  };

  const isPending = (id: string) => {
    return pendingUpdates.has(id);
  };

  const handleRefresh = () => {
    fetchData();
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw className="h-8 w-8 animate-spin" />
        <span className="ml-2">Loading relationship data...</span>
      </div>
    );
  }

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <GitMerge className="h-5 w-5 text-indigo-600" />
                <span className="text-sm font-medium">Potential Duplicates</span>
              </div>
              <div className="text-2xl font-bold text-indigo-600 mt-1">
                {counts.potentialDuplicates}
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <TreePine className="h-5 w-5 text-green-600" />
                <span className="text-sm font-medium">Existing Relationships</span>
              </div>
              <div className="text-2xl font-bold text-green-600 mt-1">
                {counts.existingRelationships}
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <Users className="h-5 w-5 text-gray-600" />
                  <span className="text-sm font-medium">Status</span>
                </div>
                <div className="text-sm text-gray-600 mt-1">
                  {isProcessing ? "Processing..." : "Ready"}
                </div>
              </div>
              <Button
                onClick={handleRefresh}
                variant="outline"
                size="sm"
                disabled={isProcessing}
              >
                <RefreshCw className={`h-4 w-4 ${isProcessing ? 'animate-spin' : ''}`} />
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Main Interface */}
        <Tabs defaultValue="duplicates" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="duplicates" className="flex items-center gap-2">
              <GitMerge className="h-4 w-4" />
              Potential Duplicates
              {counts.potentialDuplicates > 0 && (
                <Badge variant="secondary" className="ml-1">
                  {counts.potentialDuplicates}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="hierarchy" className="flex items-center gap-2">
              <TreePine className="h-4 w-4" />
              Hierarchy View
            </TabsTrigger>
            <TabsTrigger value="manual" className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Manual Linking
            </TabsTrigger>
          </TabsList>

          <TabsContent value="duplicates" className="mt-6">
            <PotentialDuplicatesList
              duplicates={potentialDuplicates}
              onLinkRecords={handleLinkRecords}
              isPending={isPending}
              isProcessing={isProcessing}
            />
          </TabsContent>

          <TabsContent value="hierarchy" className="mt-6">
            <HierarchyViewer
              hierarchyData={hierarchyData}
              onUnlinkRecord={handleUnlinkRecord}
              onLinkRecords={handleLinkRecords}
              isPending={isPending}
            />
          </TabsContent>

          <TabsContent value="manual" className="mt-6">
            <ManualLinkingControls
              onLinkRecords={handleLinkRecords}
              isProcessing={isProcessing}
            />
          </TabsContent>
        </Tabs>
      </div>
    </DndProvider>
  );
};
